// ─── Astria Vietnam — isolated English/Vietnamese language preference.
// Deliberately NOT the shared `preferredLanguage` localStorage key, and a
// separate key from Vietnam V2's own toggle (`astriaVietnamV2Lang`) — this
// category only ever supports these 2 languages, remembered on this
// device, never auto-switched, never mixed with any other lane's language
// state. Same pattern as utils/India/astriaIndiaV2Language.ts.
import { useCallback, useEffect, useState } from "react";

const ASTRIA_VIETNAM_LANGUAGE_KEY = "astriaVietnamLang";

export type AstriaVietnamLanguage = "en" | "vi";

const SUPPORTED_LANGUAGES: AstriaVietnamLanguage[] = ["en", "vi"];

export const getStoredAstriaVietnamLanguage = (): AstriaVietnamLanguage => {
  try {
    const value = localStorage.getItem(ASTRIA_VIETNAM_LANGUAGE_KEY);
    return (SUPPORTED_LANGUAGES as string[]).includes(value || "")
      ? (value as AstriaVietnamLanguage)
      : "vi";
  } catch {
    return "vi";
  }
};

export const setStoredAstriaVietnamLanguage = (
  lang: AstriaVietnamLanguage,
): void => {
  try {
    localStorage.setItem(ASTRIA_VIETNAM_LANGUAGE_KEY, lang);
  } catch {
    // ignore storage failures
  }
};

// React hook form — used by AstriaVietnamShell + each tab so the shell's
// toggle and the tab body's copy re-render together from one source of
// truth, instead of the shell holding its own disconnected state.
export function useAstriaVietnamLanguage(): [
  AstriaVietnamLanguage,
  (lang: AstriaVietnamLanguage) => void,
] {
  const [lang, setLangState] = useState<AstriaVietnamLanguage>(
    getStoredAstriaVietnamLanguage,
  );

  useEffect(() => {
    try {
      localStorage.setItem(ASTRIA_VIETNAM_LANGUAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const setLang = useCallback((next: AstriaVietnamLanguage) => {
    setLangState(next);
  }, []);

  return [lang, setLang];
}
